import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { useShop } from '../../context/ShopContext';

const { FiX, FiMinus, FiPlus, FiTrash2 } = FiIcons;

const CartDrawer = () => {
  const { cart, isCartOpen, setIsCartOpen, updateQuantity, removeFromCart } = useShop();
  
  const subtotal = cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);

  return (
    <AnimatePresence>
      {isCartOpen && (
        <>
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setIsCartOpen(false)}
            className="fixed inset-0 bg-black/20 backdrop-blur-sm z-[100]"
          />
          <motion.div 
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="fixed right-0 top-0 h-full w-full max-w-md bg-luma-bg z-[101] shadow-2xl flex flex-col"
          >
            <div className="p-6 border-b border-luma-sand flex justify-between items-center bg-white">
              <h2 className="font-serif text-2xl">Your Basket</h2>
              <button onClick={() => setIsCartOpen(false)} className="p-2 hover:bg-luma-bg rounded-full transition-colors">
                <SafeIcon icon={FiX} className="text-xl" />
              </button>
            </div>

            <div className="flex-grow overflow-y-auto p-6 space-y-6">
              {cart.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center text-center space-y-4 opacity-60">
                  <div className="w-16 h-16 bg-luma-sand/20 rounded-full flex items-center justify-center">
                    <SafeIcon icon={FiIcons.FiShoppingBag} className="text-2xl" />
                  </div>
                  <p className="font-serif text-xl">Your basket is empty</p>
                  <button 
                    onClick={() => setIsCartOpen(false)}
                    className="text-xs font-bold tracking-widest uppercase border-b border-luma-text"
                  >
                    Start Shopping
                  </button>
                </div>
              ) : (
                cart.map(item => (
                  <div key={item.id} className="flex gap-4 group">
                    <div className="w-24 h-32 bg-luma-sand/10 rounded-xl overflow-hidden shrink-0">
                      <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                    </div>
                    <div className="flex-grow space-y-1">
                      <div className="flex justify-between">
                        <h3 className="font-serif text-lg leading-tight">{item.name}</h3>
                        <button onClick={() => removeFromCart(item.id)} className="text-luma-text/30 hover:text-red-400 transition-colors">
                          <SafeIcon icon={FiTrash2} />
                        </button>
                      </div>
                      <p className="text-xs text-luma-text/50">{item.subtitle}</p>
                      <div className="flex justify-between items-center pt-4">
                        <div className="flex items-center border border-luma-sand rounded-lg px-2 py-1">
                          <button onClick={() => updateQuantity(item.id, -1)} className="p-1 hover:text-luma-primary"><SafeIcon icon={FiMinus} className="text-xs" /></button>
                          <span className="w-8 text-center text-xs font-bold">{item.quantity}</span>
                          <button onClick={() => updateQuantity(item.id, 1)} className="p-1 hover:text-luma-primary"><SafeIcon icon={FiPlus} className="text-xs" /></button>
                        </div>
                        <span className="font-medium text-sm">${(item.price * item.quantity).toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            {cart.length > 0 && (
              <div className="p-6 bg-white border-t border-luma-sand space-y-4">
                <div className="flex justify-between items-center text-luma-text/60 text-sm">
                  <span>Subtotal</span>
                  <span className="font-medium text-luma-text">${subtotal.toFixed(2)}</span>
                </div>
                <p className="text-[10px] text-luma-text/40 leading-relaxed">
                  Shipping and taxes calculated at checkout. All registry orders over $150 qualify for complimentary delivery.
                </p>
                <button className="w-full bg-luma-text text-white py-4 rounded-full text-xs font-bold tracking-widest uppercase hover:bg-luma-primary transition-all">
                  Proceed to Checkout
                </button>
              </div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default CartDrawer;